"use client"

import dynamic from "next/dynamic"
import { useEffect, useState } from "react"
import { ArrowLeft } from "lucide-react"
import { TooltipWrapper } from "@/components/TooltipWrapper"
import { BlurredContent } from "@/components/BlurredContent"
import { useAuth } from "@/contexts/AuthContext"
import { useRouter } from "next/navigation"
import { SchoolAccessProvider } from "@/contexts/SchoolAccessContext"
import AcademicResultsChart from "@/components/AcademicResultsChart"
import { DataSourceInfo } from "@/components/DataSourceInfo"
import StructuredData from "@/components/StructuredData"
import Link from 'next/link'

// Add dynamic imports with proper error handling
const TestResults = dynamic(
  () =>
    import("./TestResults").catch(() => {
      console.error("Failed to load TestResults component")
      const FallbackComponent = () => <div>Failed to load test results</div>
      FallbackComponent.displayName = "TestResultsFallback"
      return FallbackComponent
    }),
  {
    loading: () => <div className="animate-pulse h-40 bg-gray-100 rounded-lg"></div>,
    ssr: false,
  },
)

const CollapsibleSurveyResults = dynamic(
  () =>
    import("./CollapsibleSurveyResults").catch(() => {
      console.error("Failed to load CollapsibleSurveyResults component")
      const FallbackComponent = () => <div>Failed to load survey results</div>
      FallbackComponent.displayName = "SurveyResultsFallback"
      return FallbackComponent
    }),
  {
    loading: () => <div className="animate-pulse h-40 bg-gray-100 rounded-lg"></div>,
    ssr: false,
  },
)

const Complaints = dynamic(
  () =>
    import("./Complaints").catch(() => {
      console.error("Failed to load Complaints component")
      const FallbackComponent = () => <div>Failed to load complaints data</div>
      FallbackComponent.displayName = "ComplaintsFallback"
      return FallbackComponent
    }),
  {
    loading: () => <div className="animate-pulse h-40 bg-gray-100 rounded-lg"></div>,
    ssr: false,
  },
)

interface SchoolData {
  id: string | number
  skolenhetskod: string
  name: string
  kommun: string
  typ: string
  f_klass: string
  gr1: string
  gr2: string
  gr3: string
  gr4: string
  gr5: string
  gr6: string
  gr7: string
  gr8: string
  gr9: string
  totala_elever: number
  address: string
  postnr: string
  ort: string
  seoSlug: string
  meritvardeArskurs9: { [year: string]: string | null }
  salsa: string | null
  andel_utlandsk: number
  andel_eftergymnasial: number
  elever_per_larare: number
  andel_behoriga_larare: number | null | string
}

interface SurveyData {
  studentSurvey5: { [key: string]: { [key: string]: number } }
  studentSurvey8: { [key: string]: { [key: string]: number } }
  teacherSurvey: { [key: string]: { [key: string]: number } }
  parentSurvey: { [key: string]: { [key: string]: number } }
}

interface ClientSchoolPageProps {
  schoolData: SchoolData
  surveyData: SurveyData
  searchParams: { back?: string } | Promise<{ back?: string }>
}

export default function ClientSchoolPage({ schoolData, surveyData, searchParams }: ClientSchoolPageProps) {
  const [resolvedSearchParams, setResolvedSearchParams] = useState<{ back?: string }>({})
  const [isClient, setIsClient] = useState(false)
  const router = useRouter()
  const { loading } = useAuth()

  useEffect(() => {
    const resolveSearchParams = async () => {
      if (searchParams instanceof Promise) {
        try {
          const resolved = await searchParams
          setResolvedSearchParams(resolved)
        } catch (error) {
          console.error("Error resolving searchParams:", error)
          setResolvedSearchParams({})
        }
      } else if (typeof searchParams === "object" && searchParams !== null) {
        setResolvedSearchParams(searchParams)
      } else {
        console.warn("Unexpected searchParams type:", typeof searchParams)
        setResolvedSearchParams({})
      }
    }

    resolveSearchParams()
  }, [searchParams])

  useEffect(() => {
    setIsClient(true)
  }, [])

  if (!isClient || loading) {
    return <div>Loading...</div>
  }

  // Create enhanced structured data for the school
  const educationalOrgSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": schoolData.name,
    "description": `Information om ${schoolData.name} i ${schoolData.kommun}. Skolan är en ${schoolData.typ.toLowerCase()} med cirka ${Math.round(schoolData.totala_elever / 10) * 10} elever.`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": schoolData.address || "",
      "addressLocality": schoolData.ort || "",
      "postalCode": schoolData.postnr || "",
      "addressRegion": schoolData.kommun,
      "addressCountry": "SE"
    },
    "url": `https://valjratt.se/skola/enhet/${schoolData.seoSlug}`,
  }

  // Create FAQ schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `Hur många elever går på ${schoolData.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${schoolData.name} har cirka ${Math.round(schoolData.totala_elever / 10) * 10} elever.`
        }
      },
      {
        "@type": "Question",
        "name": `Vilka årskurser finns på ${schoolData.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${schoolData.name} har årskurserna ${[
            schoolData.f_klass === "J" ? "F" : null,
            schoolData.gr1 === "J" ? "1" : null,
            schoolData.gr2 === "J" ? "2" : null,
            schoolData.gr3 === "J" ? "3" : null,
            schoolData.gr4 === "J" ? "4" : null,
            schoolData.gr5 === "J" ? "5" : null,
            schoolData.gr6 === "J" ? "6" : null,
            schoolData.gr7 === "J" ? "7" : null,
            schoolData.gr8 === "J" ? "8" : null,
            schoolData.gr9 === "J" ? "9" : null,
          ].filter(Boolean).join(", ")}.`
        }
      },
      {
        "@type": "Question",
        "name": `Hur många elever per lärare har ${schoolData.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${schoolData.name} har i genomsnitt ${schoolData.elever_per_larare.toFixed(1)} elever per lärare.`
        }
      },
      {
        "@type": "Question",
        "name": `Hur bra betyg har ${schoolData.name} jämfört med andra skolor i ${schoolData.kommun}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `För att jämföra ${schoolData.name} med andra skolor i ${schoolData.kommun}, använd vårt skolsök-verktyg för att se meritvärden, akademiska resultat, trivsel, studiero och viktiga faktorer för alla skolor i kommunen.`
        }
      },
      {
        "@type": "Question",
        "name": `Hur står sig ${schoolData.name} jämfört med andra skolor i ${schoolData.kommun}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `För att jämföra ${schoolData.name} med andra skolor i ${schoolData.kommun}, använd vårt skolsök-verktyg för att se meritvärden, akademiska resultat, trivsel, studiero och viktiga faktorer för alla skolor i kommunen.`
        }
      },
      {
        "@type": "Question",
        "name": `Vad är andelen behöriga lärare på ${schoolData.name} jämfört med andra skolor i ${schoolData.kommun}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${schoolData.name} har ${schoolData.andel_behoriga_larare}% behöriga lärare.`
        }
      },
    ]
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Hem",
        "item": "https://valjratt.se/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Skolor",
        "item": "https://valjratt.se/skola"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": schoolData.kommun,
        "item": `https://valjratt.se/skola?kommun=${encodeURIComponent(schoolData.kommun)}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": schoolData.name,
        "item": `https://valjratt.se/skola/enhet/${schoolData.seoSlug}`
      }
    ]
  };

  const averageQualifiedTeachers = 73 // This is calculated manually

  // Process merit value data for past 3 years (2223, 2324, 2425) and sort chronologically (oldest to newest)
  const pastThreeYears = ["2223", "2324", "2425"]
  const meritValueData = Object.entries(schoolData.meritvardeArskurs9 || {})
    .filter(([year]) => pastThreeYears.includes(year))
    .map(([year, value]) => ({
      year,
      value: value !== null && value !== "n/a" ? Number(value) : null,
      average:
        {
          "2223": 239,
          "2324": 237,
          "2425": 237,
        }[year] || null,
    }))
    .filter((data) => data.value !== null)
    .sort((a, b) => a.year.localeCompare(b.year)) // Sort by year ascending (oldest first)

  return (
    <SchoolAccessProvider schoolId={schoolData.skolenhetskod.toString()} schoolName={schoolData.name}>
      <StructuredData data={educationalOrgSchema} />
      <StructuredData data={faqSchema} />
      <StructuredData data={breadcrumbSchema} />
      <div className="container mx-auto px-4 py-8">
        {/* Use section with role="banner" instead of header */}
        <section className="mb-4" role="banner">
          <h1 className="text-3xl font-bold mb-2">{schoolData.name}</h1>
          <h2 className="text-xl text-gray-600 mb-6">{schoolData.kommun}</h2>
        </section>
        
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex flex-wrap text-sm">
            <li className="flex items-center">
              <Link href="/" className="text-blue-600 hover:underline">
                Hem
              </Link>
              <span className="mx-2 text-gray-500">/</span>
            </li>
            <li className="flex items-center">
              <Link href="/skola" className="text-blue-600 hover:underline">
                Skolor
              </Link>
              <span className="mx-2 text-gray-500">/</span>
            </li>
            <li className="text-gray-700" aria-current="page">
              {schoolData.name}
            </li>
          </ol>
        </nav>

      {/* Main content with semantic HTML */}
        <div className="flex flex-col md:flex-row">
          <nav className="md:w-1/4 md:pr-8 mb-8 md:mb-0">
            <div className="sticky top-4 bg-white p-4 border rounded-lg shadow-md">
              <h2 className="text-lg font-semibold mb-4">Innehåll</h2>
              <ul className="space-y-2">
                <li>
                  <a href="#om-skolan" className="text-blue-600 hover:underline">
                    Om skolan
                  </a>
                </li>
                <li>
                  <a href="#akademiska-resultat" className="text-blue-600 hover:underline">
                    Akademiska resultat
                  </a>
                </li>
                <li>
                  <a href="#skolenkaten" className="text-blue-600 hover:underline">
                    Skolenkäten
                  </a>
                </li>
                <li>
                  <a href="#elever-och-personal" className="text-blue-600 hover:underline">
                    Elever och personal
                  </a>
                </li>
                <li>
                  <a href="#anmalningar" className="text-blue-600 hover:underline">
                    Anmälningar
                  </a>
                </li>
              </ul>
            </div>
          </nav>

          <main className="md:w-3/4">
            {/* 1. Basic info about the school */}
            <section id="om-skolan" className="mb-8 p-6 border rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-4">Om skolan</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Kontaktuppgifter</h3>
                  <p className="mb-1">
                    <strong>Adress:</strong> {schoolData.address}
                  </p>
                  <p className="mb-1">
                    <strong>Postnummer:</strong> {schoolData.postnr}
                  </p>
                  <p className="mb-1">
                    <strong>Ort:</strong> {schoolData.ort}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Skolinformation</h3>
                  <p className="mb-1">
                    <strong>Typ:</strong> {schoolData.typ}
                  </p>
                  <p className="mb-1">
                    <strong>Årskurser:</strong>{" "}
                    {[
                      schoolData.f_klass === "J" ? "F" : null,
                      schoolData.gr1 === "J" ? "1" : null,
                      schoolData.gr2 === "J" ? "2" : null,
                      schoolData.gr3 === "J" ? "3" : null,
                      schoolData.gr4 === "J" ? "4" : null,
                      schoolData.gr5 === "J" ? "5" : null,
                      schoolData.gr6 === "J" ? "6" : null,
                      schoolData.gr7 === "J" ? "7" : null,
                      schoolData.gr8 === "J" ? "8" : null,
                      schoolData.gr9 === "J" ? "9" : null,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              </div>
            </section>

            {/* 2. Academic results */}
            <section id="akademiska-resultat" className="mb-8 p-6 border rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-4">Akademiska resultat</h2>
              <TestResults schoolData={schoolData} />

              {/* Merit Value (9th grade average) section */}
              {meritValueData.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xl font-semibold mt-4 mb-2 flex items-center">
                    Meritvärde årskurs 9
                    <TooltipWrapper content="Meritvärdet är genomsnittet av elevernas betyg i alla ämnen, omräknat till poäng där A=20, B=17.5, C=15, D=12.5, E=10 och F=0." />
                  </h3>

                  {/* Single chart with all years, fully blurred */}
                  <figure className="max-w-xl">
                    <BlurredContent schoolId={schoolData.skolenhetskod} schoolName={schoolData.name}>
                      <AcademicResultsChart
                        data={meritValueData}
                        grade="9"
                        subject="merit"
                        schoolName={schoolData.name}
                        yAxisLabel="Meritvärde"
                        yAxisDomain={[0, 340]}
                      />
                    </BlurredContent>
                    <figcaption className="text-sm text-gray-600 mt-2">
                    Blå stapel visar skolans meritvärde. Gul linje visar genomsnittet.
                  </figcaption>
                  </figure>
                </div>
              )}
            </section>

            {/* 3. Survey results */}
            <section id="skolenkaten" className="mb-8 p-6 border rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-2">Skolenkäten</h2>
              <p className="text-sm text-gray-600 mb-4">Blå stapel är skolans värde, gul linje är genomsnittet.</p>
              {surveyData && schoolData ? (
                <CollapsibleSurveyResults surveyData={surveyData} schoolData={schoolData} />
              ) : (
                <p>Ingen enkätdata tillgänglig för denna skola.</p>
              )}
            </section>

            {/* 4. Student and staff data */}
            <section id="elever-och-personal" className="mb-8 p-6 border rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-4">Elever och personal</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center mb-1">
                    <span className="text-sm font-medium text-gray-500">Antal elever:</span>
                    <TooltipWrapper content="Alla elever, inklusive f-klass om det finns." />
                  </div>
                  <p className="text-lg font-semibold">ca {Math.round(schoolData.totala_elever / 10) * 10}</p>
                </div>
                <div>
                  <div className="flex items-center mb-1">
                    <span className="text-sm font-medium text-gray-500">Andel med utländsk bakgrund:</span>
                    <TooltipWrapper content="Utländsk bakgrund innebär att eleverna är födda utomlands eller födda i Sverige med båda föräldrarna födda utomlands." />
                  </div>
                  <BlurredContent schoolId={schoolData.skolenhetskod} schoolName={schoolData.name}>
                    <p className="text-lg font-semibold">{schoolData.andel_utlandsk.toFixed(1)}%</p>
                  </BlurredContent>
                </div>
                <div>
                  <div className="flex items-center mb-1">
                    <span className="text-sm font-medium text-gray-500">Andel med eftergymnasial utbildning:</span>
                    <TooltipWrapper content="Andel elever vars föräldrar har eftergymnasial utbildning." />
                  </div>
                  <BlurredContent schoolId={schoolData.skolenhetskod} schoolName={schoolData.name}>
                    <p className="text-lg font-semibold">{schoolData.andel_eftergymnasial.toFixed(1)}%</p>
                  </BlurredContent>
                </div>
                <div>
                  <div className="flex items-center mb-1">
                    <span className="text-sm font-medium text-gray-500">Elever per lärare:</span>
                    <TooltipWrapper content="Antal elever per lärare (heltidstjänster)." />
                  </div>
                  <p className="text-lg font-semibold">{schoolData.elever_per_larare.toFixed(1)}</p>
                </div>
                <div>
                  <div className="flex items-center mb-1">
                    <span className="text-sm font-medium text-gray-500">Andel behöriga lärare:</span>
                    <TooltipWrapper content="Andel lärare med lärarlegitimation och behörighet i minst ett ämne." />
                  </div>
                  {schoolData.andel_behoriga_larare !== null &&
                  schoolData.andel_behoriga_larare !== "#N/A" &&
                  schoolData.andel_behoriga_larare !== "n/a" ? (
                    <div>
                      <div className="relative h-6">
                        <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
                        <div
                          className="absolute inset-y-0 left-0 bg-blue-600 rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${Number(schoolData.andel_behoriga_larare)}%` }}
                        >
                          <span className="text-white text-sm font-semibold">{schoolData.andel_behoriga_larare}%</span>
                        </div>
                        <div
                          className="absolute inset-y-0 border-l-2 border-dashed border-yellow-400"
                          style={{ left: `${averageQualifiedTeachers}%` }}
                        ></div>
                      </div>
                      <div className="mt-1 text-xs text-gray-500 text-right">
                        Genomsnitt: {averageQualifiedTeachers}%
                      </div>
                    </div>
                  ) : (
                    <p className="text-lg font-semibold">n/a</p>
                  )}
                </div>
              </div>
            </section>

            {/* 5. Complaints/misconduct reports */}
            <section id="anmalningar" className="mb-8 p-6 border rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-4">Anmälningar</h2>
              <p className="mb-4 text-sm text-gray-700">
                Här visas information om anmälningar till Skolinspektionen gällande denna skola.
              </p>
              <Complaints schoolData={schoolData} />
            </section>

            {/* 6. Data sources - Move this inside the main element */}
            <section id="datakallor" className="mb-8 p-6 border rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-4">Datakällor</h2>
              <DataSourceInfo />
            </section>
          </main>
        </div>

        <button
          onClick={() => {
            const backParam = resolvedSearchParams.back
            if (backParam) {
              router.push(`/skola?${backParam}`)
            } else {
              router.push("/skola")
            }
          }}
          className="text-blue-600 hover:underline flex items-center bg-transparent"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Tillbaka till skolsök
        </button>
      </div>
    </SchoolAccessProvider>
  )
}

